import React, { useCallback, useEffect, useState } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";
import vendorTypeService from "./vendorTypeService";

const emptyForm = {
  name: "",
  code: "",
  description: "",
  is_active: true,
};

export default function VendorTypes() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const result = await vendorTypeService.getAll({
        search: search || undefined,
      });

      const payload = result?.data;
      setItems(Array.isArray(payload) ? payload : payload?.data || []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Could not load vendor types. Check the API route and authentication."
      );
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError("");
    setNotice("");
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      code: item.code || "",
      description: item.description || "",
      is_active: Boolean(item.is_active),
    });
    setError("");
    setNotice("");
    setModalOpen(true);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setNotice("");

    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim(),
        description: form.description.trim() || null,
        is_active: Boolean(form.is_active),
      };

      if (editingId) {
        await vendorTypeService.update(editingId, payload);
        setNotice("Vendor type updated successfully.");
      } else {
        await vendorTypeService.create(payload);
        setNotice("Vendor type created successfully.");
      }

      setModalOpen(false);
      setForm(emptyForm);
      await loadItems();
    } catch (err) {
      const validationErrors = err?.response?.data?.errors;
      const firstValidationError = validationErrors
        ? Object.values(validationErrors).flat()[0]
        : null;

      setError(
        firstValidationError ||
          err?.response?.data?.message ||
          "Unable to save vendor type."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete vendor type "${item.name}"?`)) return;

    setError("");
    setNotice("");

    try {
      await vendorTypeService.delete(item.id);
      setNotice("Vendor type deleted successfully.");
      await loadItems();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to delete vendor type."
      );
    }
  };

  const activeCount = items.filter((item) => item.is_active).length;

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="flex">
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((value) => !value)}
        />

        <main
          className={`mt-16 min-w-0 flex-1 transition-layout ${
            sidebarCollapsed ? "ml-16" : "ml-72"
          }`}
        >
          <div className="p-6 lg:p-8">
            <Breadcrumb />

            <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                  <Icon name="Tags" size={24} className="text-primary" />
                </div>

                <div>
                  <h1 className="text-3xl font-bold text-foreground">
                    Vendor Types
                  </h1>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Configure and manage the types used to classify vendors.
                  </p>
                </div>
              </div>

              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={openCreate}
              >
                Add Vendor Type
              </Button>
            </div>

            {error && !modalOpen && (
              <div className="mb-4 rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                {error}
              </div>
            )}

            {notice && (
              <div className="mb-4 rounded-lg border border-success/20 bg-success/10 p-4 text-sm text-success">
                {notice}
              </div>
            )}

            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              <StatCard label="Vendor Types" value={items.length} icon="Tags" />
              <StatCard
                label="Active Types"
                value={activeCount}
                icon="CheckCircle"
              />
              <StatCard
                label="Inactive Types"
                value={items.length - activeCount}
                icon="CircleSlash"
              />
            </div>

            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <div className="flex flex-wrap items-center justify-between gap-4 border-b border-border p-5">
                <div>
                  <h2 className="font-semibold text-foreground">
                    Vendor Type Directory
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    All vendor types are loaded from the Laravel API.
                  </p>
                </div>

                <div className="relative">
                  <Icon
                    name="Search"
                    size={17}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search name or code..."
                    className="w-64 rounded-lg border border-border bg-background py-2 pl-9 pr-3 text-sm"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/20">
                      {["Name", "Code", "Description", "Status", "Actions"].map(
                        (heading) => (
                          <th
                            key={heading}
                            className="px-5 py-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                          >
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {loading ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-5 py-12 text-center text-muted-foreground"
                        >
                          Loading vendor types...
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="px-5 py-12 text-center text-muted-foreground"
                        >
                          No vendor types found.
                        </td>
                      </tr>
                    ) : (
                      items.map((item) => (
                        <tr
                          key={item.id}
                          className="border-b border-border last:border-0 hover:bg-muted/20"
                        >
                          <td className="px-5 py-4 font-medium text-foreground">
                            {item.name}
                          </td>
                          <td className="px-5 py-4 text-sm">{item.code}</td>
                          <td className="max-w-md px-5 py-4 text-sm text-muted-foreground">
                            {item.description || "—"}
                          </td>
                          <td className="px-5 py-4">
                            <span
                              className={`rounded-full px-3 py-1 text-xs font-medium ${
                                item.is_active
                                  ? "bg-success/10 text-success"
                                  : "bg-muted text-muted-foreground"
                              }`}
                            >
                              {item.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            <div className="flex gap-2">
                              <button
                                type="button"
                                title="Edit"
                                onClick={() => openEdit(item)}
                                className="rounded-lg p-2 hover:bg-muted"
                              >
                                <Icon name="Pencil" size={17} />
                              </button>
                              <button
                                type="button"
                                title="Delete"
                                onClick={() => handleDelete(item)}
                                className="rounded-lg p-2 text-error hover:bg-error/10"
                              >
                                <Icon name="Trash2" size={17} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </main>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          <button
            type="button"
            aria-label="Close modal"
            onClick={() => setModalOpen(false)}
            className="absolute inset-0 bg-black/50"
          />

          <div className="relative flex h-full w-full max-w-xl flex-col bg-background shadow-2xl">
            <div className="flex items-center justify-between border-b border-border p-6">
              <div>
                <h2 className="text-xl font-semibold text-foreground">
                  {editingId ? "Edit Vendor Type" : "Add Vendor Type"}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Enter the vendor type details below.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setModalOpen(false)}
                className="rounded-lg p-2 hover:bg-muted"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSave} className="flex-1 overflow-y-auto">
              <div className="space-y-5 p-6">
                {error && (
                  <div className="rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                    {error}
                  </div>
                )}

                <Field label="Vendor Type Name *">
                  <input
                    required
                    maxLength={100}
                    value={form.name}
                    onChange={(event) =>
                      setForm({ ...form, name: event.target.value })
                    }
                    placeholder="e.g. Supplier"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Vendor Type Code *">
                  <input
                    required
                    maxLength={50}
                    value={form.code}
                    onChange={(event) =>
                      setForm({ ...form, code: event.target.value })
                    }
                    placeholder="e.g. SUPPLIER"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  />
                </Field>

                <Field label="Description">
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={(event) =>
                      setForm({ ...form, description: event.target.value })
                    }
                    placeholder="Describe this vendor type..."
                    className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                  />
                </Field>

                <label className="flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(event) =>
                      setForm({ ...form, is_active: event.target.checked })
                    }
                    className="h-4 w-4"
                  />
                  Active
                </label>
              </div>

              <div className="sticky bottom-0 flex justify-end gap-3 border-t border-border bg-card p-5">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "Update Vendor Type"
                    : "Create Vendor Type"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="mt-2 text-2xl font-bold text-foreground">{value}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon name={icon} size={21} />
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}